import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";

interface PublicLayoutProps {
  children: React.ReactNode;
}

/**
 * Shared header + footer for public marketing and SEO landing pages.
 * Matches Marketing page styling; no layout or visual changes.
 */
export function PublicLayout({ children }: PublicLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-wt-bg-primary text-wt-text-primary">
      <header className="sticky top-0 z-50 bg-wt-bg-primary/95 backdrop-blur-sm border-b border-wt-border-default">
        <div className="mx-auto max-w-7xl px-4 py-4 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <Logo variant="fullTransparent" size="h-8" />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary transition-colors"
              >
                Home
              </Link>
              <Link
                to="/login"
                className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary transition-colors"
              >
                Sign In
              </Link>
              <a href="/#installer-access">
                <Button className="h-9 bg-primary px-6 font-sans text-sm font-semibold text-wt-bg-primary hover:bg-wt-green-muted">
                  Request Installer Access
                </Button>
              </a>
            </nav>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden h-9 w-9 text-wt-text-primary hover:bg-white/10"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[280px] bg-wt-bg-primary border-wt-border-default"
              >
                <div className="flex flex-col gap-4 pt-6">
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary"
                  >
                    Home
                  </Link>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary"
                  >
                    Sign In
                  </Link>
                  <a
                    href="/#installer-access"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className="h-9 bg-primary px-6 font-sans text-sm font-semibold text-wt-bg-primary hover:bg-wt-green-muted w-fit">
                      Request Installer Access
                    </Button>
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-wt-border-default px-4 py-12 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="flex flex-col gap-3">
              <Logo
                variant="fullTransparent"
                size="h-8"
                className="opacity-80"
              />
              <p className="font-sans text-sm italic text-wt-text-muted">
                Built for the professionals who keep systems running.
              </p>
            </div>
            <div>
              <p className="mb-4 font-sans text-sm text-wt-text-muted">
                © 2025 Watt Tower
              </p>
              <nav className="flex flex-wrap gap-6">
                <Link
                  to="/privacy"
                  className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary"
                >
                  Privacy
                </Link>
                <Link
                  to="/terms"
                  className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary"
                >
                  Terms of Service
                </Link>
                <a
                  href="mailto:support@watttower.com"
                  className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary"
                >
                  Contact
                </a>
              </nav>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
